import { useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTheme } from '../../contexts/ThemeContext'

type CollapsibleCardProps = {
  icon: ReactNode
  iconBgClass?: string
  title: string
  subtitle?: string
  defaultOpen?: boolean
  collapsedAsRow?: boolean
  children: ReactNode
}

const CollapsibleCard = ({
  icon,
  iconBgClass = 'bg-primary-50 text-primary-600',
  title,
  subtitle,
  defaultOpen = true,
  collapsedAsRow = false,
  children,
}: CollapsibleCardProps) => {
  const [open, setOpen] = useState(defaultOpen)
  const { theme } = useTheme()

  return (
    <section
      className="rounded-xl shadow-sm overflow-hidden"
      style={{
        backgroundColor: theme.colors.cardBackground,
        border: `1px solid ${theme.colors.cardBorder}`,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
        style={{ color: theme.colors.textPrimary }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.tableRowHover
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent'
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBgClass}`}
          >
            {icon}
          </span>
          <div className="min-w-0">
            <h3
              className="text-sm sm:text-base font-semibold truncate"
              style={{ color: theme.colors.textPrimary }}
            >
              {title}
            </h3>
            {subtitle && (
              <p
                className="text-xs sm:text-sm truncate"
                style={{ color: theme.colors.textSecondary }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <span className="shrink-0" style={{ color: theme.colors.textSecondary }}>
          {open ? (
            <ChevronDown size={18} />
          ) : collapsedAsRow ? (
            <ChevronRight size={18} />
          ) : (
            <ChevronDown size={18} />
          )}
        </span>
      </button>
      {open && (
        <div
          className="px-4 sm:px-5 py-4 sm:py-5"
          style={{ borderTop: `1px solid ${theme.colors.border}` }}
        >
          {children}
        </div>
      )}
    </section>
  )
}

export default CollapsibleCard
